import React from 'react';
import { ChevronRight, Hourglass, TriangleAlert, type LucideIcon } from 'lucide-react';
import {
  formatCount as n,
  type PipelineStage,
  type PipelineStageId,
  type StageTone,
} from '../../lib/pipelineStages';
import PipelineIcon from './PipelineIcons';

interface PipelineFlowProps {
  stages: PipelineStage[];
  /** Name of the list for assistive technology. */
  label: string;
  /** A stage opens something when this is given; without it the stages are plain blocks. */
  onSelect?: (id: PipelineStageId) => void;
  /** The counters have not been read yet, or the read failed: show a dash and say so, never a zero that is not real. */
  unread?: 'loading' | 'error';
  /**
   * Leave out the counts that are zero. The Overview keeps them ("0 escalated" is a measure, over all the tenant); a single
   * campaign has few things, so a column of zeros there is noise.
   */
  hideZeros?: boolean;
}

/** A count that waits on a person or failed carries an icon as well as its colour (DESIGN.md, colour rule 5). */
export const TONE_ICON: Record<StageTone, LucideIcon> = {
  need: Hourglass,
  bad: TriangleAlert,
};

/** What a screen reader hears after a count whose colour and icon it cannot see. */
export const TONE_WORDS: Record<StageTone, string> = { need: ', waits on a person', bad: ', failed' };

/** What an unread stage says, in the words the Key Operating Metrics use. */
const UNREAD_NOTE = { loading: 'Loading…', error: 'Could not be loaded' } as const;

/**
 * The seven stages as one sequence: a single panel with a rail of stage tiles along the top, joined by
 * hairline arrows, and under each node its column (name, figure, what the stage holds back), with the timings in one
 * band along the foot. The row never wraps: when the page is narrower than seven columns need, the panel
 * scrolls sideways. The Overview shows everyone's figures here and the campaign screen one campaign's.
 *
 * The spaces between the spans are real text, so the name read without the stylesheet still separates
 * the stage, its figure and its counts; the stage number is left out of that name (the list says "n of 7").
 */
const PipelineFlow: React.FC<PipelineFlowProps> = ({ stages, label, onSelect, unread, hideZeros }) => (
  <ol className={`c3-pipe${unread ? ' is-unread' : ''}`} aria-label={label}>
    {stages.map((stage, i) => {
      // No timing is measured yet: seven "p50 —" say nothing, so the band is left out until one exists.
      const hasTimings = stages.some((s) => s.p50 !== '—' || (s.confidence != null && s.confidence !== '—'));
      const attention = unread ? undefined : stage.attention;
      const splits = hideZeros ? stage.splits.filter((split) => split.value > 0) : stage.splits;
      const note = hideZeros && /^0 /.test(stage.note) ? '' : stage.note;
      const body = (
        <>
          <span className="c3-pipe-node" aria-hidden>
            <PipelineIcon id={stage.id} size={22} />
            {attention ? <span className={`c3-pipe-node-dot is-${attention}`} /> : null}
          </span>{' '}
          <span className="c3-pipe-card-head">
            <span className="c3-pipe-card-idx" aria-hidden>
              {String(stage.index).padStart(2, '0')}
            </span>{' '}
            <span className="c3-pipe-card-title">{stage.title}</span>
          </span>{' '}
          <span className="c3-pipe-card-out">
            <span className="c3-pipe-card-value">{unread ? '—' : n(stage.value)}</span>{' '}
            <span className="c3-pipe-card-unit">{stage.unit}</span>
          </span>{' '}
          <span className="c3-pipe-card-detail">
            {unread
              ? null
              : splits.map((split) => {
                  const ToneIcon = split.tone ? TONE_ICON[split.tone] : null;
                  return (
                    <React.Fragment key={split.key}>
                      <span className={`c3-pipe-split${split.tone ? ` is-${split.tone}` : ''}`}>
                        {ToneIcon ? <ToneIcon size={12} strokeWidth={1.5} aria-hidden /> : null}
                        <span>
                          <b>{n(split.value)}</b> {split.label}
                          {split.tone ? <span className="sr-only">{TONE_WORDS[split.tone]}</span> : null}
                        </span>
                      </span>{' '}
                    </React.Fragment>
                  );
                })}
            {unread ? <span className="c3-pipe-card-note">{UNREAD_NOTE[unread]}</span> : null}
            {!unread && note ? <span className="c3-pipe-card-note">{note}</span> : null}
          </span>{' '}
          {hasTimings ? (
            <span className="c3-pipe-card-meta">
              <span>p50 {stage.p50}</span>{' '}
              {stage.confidence != null ? <span>confidence {stage.confidence}</span> : null}
            </span>
          ) : null}
        </>
      );
      return (
        <li
          key={stage.id}
          className="c3-pipe-item"
          data-stage={stage.id}
          style={{ '--pipe-i': i } as React.CSSProperties}
        >
          {onSelect ? (
            <button type="button" className="c3-pipe-card" onClick={() => onSelect(stage.id)}>
              {body}
            </button>
          ) : (
            <div className="c3-pipe-card">{body}</div>
          )}
          {i < stages.length - 1 ? (
            <span className="c3-pipe-link" aria-hidden>
              <ChevronRight size={16} strokeWidth={1.5} />
            </span>
          ) : null}
        </li>
      );
    })}
  </ol>
);

export default PipelineFlow;
